import * as React from "react"
import {
  Modal,
  ModalCard,
  StyledModal,
  StyledModalBody,
  StyledModalActions,
  Button,
  StyledModalHeader,
  useShowSuccessToast,
} from "gatsby-interface"
import { Text } from "./shared/Text"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import {
  ui as uiText,
  gatsbyCloudCdnIntegrationConfig as text,
  errorAlert as errorText,
} from "@modules/locales/default.js"

import {
  useSetCloudCdnDomainAsPrimaryMutation,
  HostingDetailsForSiteDocument,
} from "@modules/cdn/gatsbyCloudCdn/queries.generated"

import { FormattedMessage, commonMessageTags } from "@modules/locales"
import { interpolateMessage } from "@modules/locales"

export type MakeDomainPrimaryFormProps = {
  siteId: string
  domain: string
  onDismiss: () => void
  onComplete: () => void
}

export function MakeDomainPrimaryForm({
  siteId,
  domain,
  onDismiss,
  onComplete,
}: MakeDomainPrimaryFormProps) {
  const [
    setCloudCdnDomainAsPrimaryMutation,
  ] = useSetCloudCdnDomainAsPrimaryMutation()
  const showSuccessToast = useShowSuccessToast()
  const [setError, errorAlert] = useTriggerErrorAlert()
  const [loading, setLoading] = React.useState(false)

  const handleAction = async () => {
    setLoading(true)

    setCloudCdnDomainAsPrimaryMutation({
      variables: {
        siteId,
        domainName: domain,
      },
      refetchQueries: [
        {
          query: HostingDetailsForSiteDocument,
          variables: { siteId },
        },
      ],
    })
      .then(({ data }) => {
        setLoading(false)
        const success = data?.setCloudCdnDomainAsPrimary?.success ?? false

        if (success) {
          onComplete()
          return showSuccessToast(
            <FormattedMessage<"domain", "strong">
              message={text.messages.makeDomainPrimary}
              values={{ domain }}
              tags={commonMessageTags}
            />
          )
        }

        const message =
          data?.setCloudCdnDomainAsPrimary?.message ||
          errorText.messages.somethingWentWrong
        setError({ message })
      })
      .catch(e => {
        setLoading(false)
        setError(e)
      })
  }

  return (
    <Modal
      isOpen={Boolean(domain)}
      aria-label={interpolateMessage<"domain">(
        text.messages.doYouWantToMakeDomainPrimary,
        { domain: domain }
      )}
      onDismiss={onDismiss}
      type="info"
    >
      <ModalCard>
        <StyledModal variant="ACTION">
          <StyledModalHeader onCloseButtonClick={onDismiss}>
            {text.headers.makeDomainPrimary}
          </StyledModalHeader>

          <StyledModalBody>
            <Text>
              <FormattedMessage<"domain", "strong">
                message={text.messages.areYouSureYouWantToMakeDomainPrimary}
                values={{ domain }}
                tags={commonMessageTags}
              />
            </Text>

            {errorAlert && (
              <div
                css={theme => ({
                  marginTop: theme.space[5],
                })}
              >
                {errorAlert}
              </div>
            )}

            <StyledModalActions>
              <Button type="button" onClick={onDismiss} variant="SECONDARY">
                {uiText.actions.cancel}
              </Button>
              <Button
                type="button"
                onClick={handleAction}
                tone="BRAND"
                variant="PRIMARY"
                loading={loading}
                loadingLabel={uiText.labels.updating}
              >
                {text.actions.makePrimary}
              </Button>
            </StyledModalActions>
          </StyledModalBody>
        </StyledModal>
      </ModalCard>
    </Modal>
  )
}
